import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronRight, RefreshCw, Search, Clock } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { getParties, getPartiesWithNoOrders, getPartiesWithUnpaidInvoices } from '@/app/services/partyService';
import { useAuthStore } from '@/app/store/authStore';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { cn } from '@/lib/utils';
import type { PartyWithLastOrder, PartyWithUnpaidInfo } from '@/app/types/party';

export default function PartiesPage() {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchParams] = useSearchParams();
    const user = useAuthStore((state) => state.user);

    // Get the user's managed party id to filter responsible parties
    const responsiblePartyId = user?.managed_parties?.[0]?.id;
    const hasResponsibleParties = !!responsiblePartyId;

    // Read filter params from URL
    const noOrderDays = searchParams.get('noOrderDays');
    const noOrderDaysMax = searchParams.get('noOrderDaysMax');
    const neverOrdered = searchParams.get('neverOrdered') === 'true';
    const unpaidDays = searchParams.get('unpaidDays');
    const unpaidDaysMax = searchParams.get('unpaidDaysMax');
    const isFiltered = noOrderDays !== null || neverOrdered;
    const isUnpaidFilter = unpaidDays !== null;

    // Determine page title based on filter
    const getPageTitle = () => {
        if (neverOrdered) return 'Never Ordered';
        if (unpaidDays && unpaidDaysMax && parseInt(unpaidDaysMax) < 365) {
            return `Unpaid: ${unpaidDays}-${parseInt(unpaidDaysMax) - 1} Days`;
        }
        if (unpaidDays) return `Unpaid: ${unpaidDays}+ Days`;
        if (noOrderDays && noOrderDaysMax && parseInt(noOrderDaysMax) < 365) {
            return `No Orders: ${noOrderDays}-${parseInt(noOrderDaysMax) - 1} Days`;
        }
        if (noOrderDays) return `No Orders: ${noOrderDays}+ Days`;
        return 'All Parties';
    };

    const { data, isLoading, refetch, isFetching } = useQuery({
        queryKey: ['parties', searchQuery, responsiblePartyId, noOrderDays, noOrderDaysMax, neverOrdered, unpaidDays, unpaidDaysMax],
        queryFn: () => {
            if (isUnpaidFilter) {
                return getPartiesWithUnpaidInvoices(
                    parseInt(unpaidDays!),
                    100,
                    0,
                    responsiblePartyId,
                    searchQuery || undefined,
                    unpaidDaysMax ? parseInt(unpaidDaysMax) : undefined
                );
            }
            if (neverOrdered) {
                // Special case: fetch parties that never ordered
                // Using a very large number to include all, with never_ordered flag
                return getPartiesWithNoOrders(
                    36500, // ~100 years, effectively "all time"
                    100,
                    0,
                    responsiblePartyId,
                    searchQuery || undefined,
                    true // include never ordered
                ).then(response => {
                    // Filter to only never ordered (last_order_date is null)
                    return {
                        ...response,
                        items: response.items.filter(p => p.last_order_date === null)
                    };
                });
            }
            if (noOrderDays) {
                return getPartiesWithNoOrders(
                    parseInt(noOrderDays),
                    100,
                    0,
                    responsiblePartyId,
                    searchQuery || undefined,
                    false, // don't include never ordered in day-based filters
                    noOrderDaysMax ? parseInt(noOrderDaysMax) : undefined
                );
            }
            return getParties(100, 0, searchQuery || undefined, responsiblePartyId);
        },
        refetchInterval: 60000,
        enabled: hasResponsibleParties, // Only fetch if user has responsible parties
    });

    const parties = data?.items || [];

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader
                title={getPageTitle()}
                showBack
                backPath="/parties"
                rightAction={
                    <button
                        onClick={() => refetch()}
                        className="flex items-center justify-center w-10 h-10 rounded-full active:bg-accent"
                        disabled={isFetching}
                    >
                        <RefreshCw
                            className={cn(
                                "h-5 w-5 text-muted-foreground",
                                isFetching && "animate-spin"
                            )}
                        />
                    </button>
                }
            />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                {!hasResponsibleParties ? (
                    <MobileEmptyState
                        icon={<Building2 className="h-16 w-16" />}
                        title="No Parties Assigned"
                        description="You are not responsible for any parties"
                    />
                ) : (
                    <>
                        {/* Search input */}
                        <div className="p-4 pb-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search parties..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="mobile-input pl-10"
                                />
                            </div>
                        </div>

                        {isLoading ? (
                            <PartySkeleton />
                        ) : parties.length === 0 ? (
                            <MobileEmptyState
                                icon={<Building2 className="h-16 w-16" />}
                                title="No Parties Found"
                                description={searchQuery
                                    ? "No parties match your search"
                                    : "No parties match this filter"}
                            />
                        ) : (
                    <div className="p-4 pt-2 space-y-3">
                        {parties.map((party) => {
                            // Type guards
                            const partyWithOrder = party as PartyWithLastOrder;
                            const hasLastOrderInfo = 'days_since_last_order' in party;
                            const partyWithUnpaid = party as PartyWithUnpaidInfo;
                            const hasUnpaidInfo = 'unpaid_invoice_count' in party;

                            return (
                                <Link
                                    key={party.id}
                                    to={`/parties/${party.id}`}
                                    className="block"
                                >
                                    <div className="mobile-card active:bg-accent transition-colors">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                {/* Avatar */}
                                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-lg font-semibold text-primary">
                                                        {party.name.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-foreground truncate">
                                                        {party.name}
                                                    </p>
                                                    {party.phone && (
                                                        <p className="text-sm text-muted-foreground truncate">
                                                            {party.phone}
                                                        </p>
                                                    )}
                                                    {party.billing_address && (
                                                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                                                            {party.billing_address}
                                                        </p>
                                                    )}
                                                    {/* Show unpaid info when in unpaid filter mode */}
                                                    {isUnpaidFilter && hasUnpaidInfo && (
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <span className="text-xs text-red-600 font-medium">
                                                                {partyWithUnpaid.unpaid_invoice_count} invoice{partyWithUnpaid.unpaid_invoice_count !== 1 ? 's' : ''} &middot; ₹{Number(partyWithUnpaid.total_amount_due || 0).toLocaleString('en-IN')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {/* Show days since last order when filtered */}
                                                    {isFiltered && !isUnpaidFilter && hasLastOrderInfo && (
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <Clock className="h-3 w-3 text-orange-500" />
                                                            <span className="text-xs text-orange-600 font-medium">
                                                                {partyWithOrder.days_since_last_order === null
                                                                    ? 'Never ordered'
                                                                    : `${partyWithOrder.days_since_last_order} days ago`}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function PartySkeleton() {
    return (
        <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="mobile-card">
                    <div className="flex items-center gap-3">
                        <MobileSkeleton className="w-12 h-12 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <MobileSkeleton className="h-5 w-32" />
                            <MobileSkeleton className="h-4 w-24" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
